import React, { useEffect, useState } from 'react';
import { FaTimes, FaDownload, FaMobileAlt } from 'react-icons/fa';
import { FiShare, FiPlusSquare } from 'react-icons/fi';
import { useInstallPrompt, promptInstall, SHOW_INSTALL_EVENT } from '../../utils/installPrompt';

const DISMISS_KEY = 'installPromptDismissedAt';
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 3000;

const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY), 10);
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // storage unavailable (private mode): banner will simply show again next visit
  }
};

const Step = ({ number, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center">
      {number}
    </span>
    <span className="text-sm text-gray-700 leading-6">{children}</span>
  </li>
);

const InstructionSteps = ({ platform }) => {
  if (platform === 'ios-in-app') {
    return (
      <p className="text-sm text-gray-700">
        This page is open inside another app. Tap the <strong>⋯</strong> or <strong>Share</strong> menu and choose{' '}
        <strong>Open in Safari</strong>, then install from there.
      </p>
    );
  }

  if (platform === 'mac-safari') {
    return (
      <ol className="space-y-2">
        <Step number={1}>In the menu bar, open <strong>File</strong>.</Step>
        <Step number={2}>Choose <strong>Add to Dock…</strong></Step>
        <Step number={3}>Click <strong>Add</strong>.</Step>
      </ol>
    );
  }

  return (
    <ol className="space-y-2">
      <Step number={1}>
        Tap the <strong>Share</strong> button <FiShare className="inline-block align-text-bottom text-blue-500" aria-label="Share" /> in Safari's toolbar.
      </Step>
      <Step number={2}>
        Scroll down and tap <strong>Add to Home Screen</strong> <FiPlusSquare className="inline-block align-text-bottom" aria-label="Add" />.
      </Step>
      <Step number={3}>Tap <strong>Add</strong>. Cosmo Fleets will appear on your home screen.</Step>
    </ol>
  );
};

const InstallPrompt = () => {
  const { canInstall, canPrompt, platform } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  // Auto-show once per visit after a short delay, unless dismissed recently
  useEffect(() => {
    if (!canInstall || wasRecentlyDismissed()) return undefined;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [canInstall]);

  // Sidebar "Install app" opens the prompt on demand, ignoring a previous dismissal
  useEffect(() => {
    const open = () => {
      setVisible(true);
      setShowSteps(platform !== 'other');
    };
    window.addEventListener(SHOW_INSTALL_EVENT, open);
    return () => window.removeEventListener(SHOW_INSTALL_EVENT, open);
  }, [platform]);

  if (!visible || !canInstall) return null;

  const dismiss = () => {
    rememberDismissal();
    setVisible(false);
    setShowSteps(false);
  };

  const handleInstall = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') setVisible(false);
      return;
    }
    setShowSteps(true);
  };

  const subtitle = platform === 'mac-safari'
    ? 'Add it to your Dock for one-click access.'
    : 'Add it to your home screen for quick, full-screen access.';

  return (
    <div
      role="dialog"
      aria-labelledby="install-prompt-title"
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-96 bg-white rounded-2xl shadow-xl border border-gray-100 p-4"
    >
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h2 id="install-prompt-title" className="text-sm font-semibold text-gray-900">Install Cosmo Fleets</h2>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600 p-1 -m-1"
          aria-label="Dismiss install prompt"
        >
          <FaTimes />
        </button>
      </div>

      {showSteps ? (
        <div className="mt-4">
          <InstructionSteps platform={platform} />
          <button
            type="button"
            onClick={dismiss}
            className="mt-4 w-full text-sm font-medium text-gray-600 hover:text-gray-800 py-2 rounded-xl border border-gray-200"
          >
            Got it
          </button>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="flex-1 text-sm font-medium text-gray-600 hover:text-gray-800 py-2 rounded-xl border border-gray-200"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleInstall}
            className="flex-1 flex items-center justify-center gap-2 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-xl transition-colors"
          >
            {canPrompt ? <FaDownload /> : <FaMobileAlt />}
            {canPrompt ? 'Install' : 'Show me how'}
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
